import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gsap } from 'gsap';

type TypingAction =
  | { type: 'delete'; delay: number }
  | { type: 'type'; delay: number; nextChar: string }
  | { type: 'startDeleting'; delay: number }
  | { type: 'advance'; nextTextIndex: number; shouldStop: boolean }
  | { type: 'complete' };

interface UseTextAnimationOptions {
  text: string | string[];
  typingSpeed?: number;
  initialDelay?: number;
  pauseDuration?: number;
  deletingSpeed?: number;
  loop?: boolean;
  variableSpeed?: { min: number; max: number };
  showCursor?: boolean;
  cursorBlinkDuration?: number;
  startOnVisible?: boolean;
  reverseMode?: boolean;
  onSentenceComplete?: (sentence: string, index: number) => void;
  onComplete?: () => void;
}

export interface UseTextAnimationResult {
  displayedText: string;
  currentTextIndex: number;
  currentCharIndex: number;
  isDeleting: boolean;
  textArray: string[];
  setContainerElement: (el: HTMLElement | null) => void;
  cursorRef: React.RefObject<HTMLSpanElement | null>;
}

function getTypingAction({
  isDeleting,
  displayedText,
  currentCharIndex,
  processedText,
  textArrayLength,
  currentTextIndex,
  loop,
  typingSpeed,
  initialDelay,
  pauseDuration,
  variableSpeed,
  getRandomSpeed,
  deletingSpeed
}: {
  isDeleting: boolean;
  displayedText: string;
  currentCharIndex: number;
  processedText: string;
  textArrayLength: number;
  currentTextIndex: number;
  loop: boolean;
  typingSpeed: number;
  initialDelay: number;
  pauseDuration: number;
  variableSpeed?: { min: number; max: number };
  getRandomSpeed: () => number;
  deletingSpeed: number;
}): TypingAction | null {
  if (isDeleting) {
    if (displayedText === '') {
      const isLast = currentTextIndex === textArrayLength - 1;
      return {
        type: 'advance',
        nextTextIndex: (currentTextIndex + 1) % textArrayLength,
        shouldStop: isLast && !loop
      };
    }
    return { type: 'delete', delay: deletingSpeed };
  }

  if (currentCharIndex < processedText.length) {
    const shouldDelayStart = currentCharIndex === 0 && displayedText === '';
    const startDelay = shouldDelayStart ? initialDelay : 0;
    const speed = variableSpeed ? getRandomSpeed() : typingSpeed;
    return {
      type: 'type',
      delay: startDelay + speed,
      nextChar: processedText[currentCharIndex]
    };
  }

  if (textArrayLength >= 1) {
    if (!loop && currentTextIndex === textArrayLength - 1) {
      return { type: 'complete' };
    }
    return { type: 'startDeleting', delay: pauseDuration };
  }

  return null;
}

export function useTextAnimation({
  text,
  typingSpeed = 40,
  initialDelay = 0,
  pauseDuration = 2000,
  deletingSpeed = 30,
  loop = true,
  variableSpeed,
  showCursor = true,
  cursorBlinkDuration = 0.5,
  startOnVisible = false,
  reverseMode = false,
  onSentenceComplete,
  onComplete
}: UseTextAnimationOptions): UseTextAnimationResult {
  const [displayedText, setDisplayedText] = useState('');
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(!startOnVisible);
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  const cursorRef = useRef<HTMLSpanElement | null>(null);

  const textArray = useMemo(() => (Array.isArray(text) ? text : [text]), [text]);

  const getRandomSpeed = useCallback(() => {
    if (!variableSpeed) return typingSpeed;
    const { min, max } = variableSpeed;
    const range = max - min;
    if (range <= 0) return min;
    const browserCrypto = globalThis.crypto as Crypto | undefined;
    if (typeof browserCrypto?.getRandomValues !== 'function') {
      return min;
    }
    const values = new Uint32Array(1);
    browserCrypto.getRandomValues(values);
    const normalized = values[0] / (0xffffffff + 1);
    return min + normalized * range;
  }, [variableSpeed, typingSpeed]);

  const handleDeleteTick = useCallback(() => {
    setDisplayedText(prev => prev.slice(0, -1));
  }, []);

  const handleTypeTick = useCallback((nextChar: string) => {
    setDisplayedText(prev => prev + nextChar);
    setCurrentCharIndex(prev => prev + 1);
  }, []);

  const handleStartDeleting = useCallback(() => {
    setIsDeleting(true);
  }, []);

  const handleAdvance = useCallback(
    (
      action: Extract<TypingAction, { type: 'advance' }>,
      sentence: string,
      sentenceIndex: number
    ) => {
      setIsDeleting(false);
      if (action.shouldStop) return;
      if (onSentenceComplete) {
        onSentenceComplete(sentence, sentenceIndex);
      }
      setCurrentTextIndex(action.nextTextIndex);
      setCurrentCharIndex(0);
    },
    [onSentenceComplete]
  );

  useEffect(() => {
    if (!startOnVisible || !containerElement) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(containerElement);
    return () => {
      observer.disconnect();
    };
  }, [startOnVisible, containerElement]);

  useEffect(() => {
    if (!showCursor || !cursorRef.current) return;
    gsap.set(cursorRef.current, { opacity: 1 });
    const tween = gsap.to(cursorRef.current, {
      opacity: 0,
      duration: cursorBlinkDuration,
      repeat: -1,
      yoyo: true,
      ease: 'power2.inOut'
    });
    return () => {
      tween.kill();
    };
  }, [showCursor, cursorBlinkDuration]);

  useEffect(() => {
    if (!isVisible) return;

    let timeout: NodeJS.Timeout;

    const currentText = textArray[currentTextIndex];
    const processedText = reverseMode ? currentText.split('').toReversed().join('') : currentText;
    const action = getTypingAction({
      isDeleting,
      displayedText,
      currentCharIndex,
      processedText,
      textArrayLength: textArray.length,
      currentTextIndex,
      loop,
      typingSpeed,
      initialDelay,
      pauseDuration,
      variableSpeed,
      getRandomSpeed,
      deletingSpeed
    });

    if (!action) return;

    switch (action.type) {
      case 'delete':
        timeout = setTimeout(handleDeleteTick, action.delay);
        break;
      case 'type':
        timeout = setTimeout(handleTypeTick, action.delay, action.nextChar);
        break;
      case 'startDeleting':
        timeout = setTimeout(handleStartDeleting, action.delay);
        break;
      case 'advance':
        timeout = setTimeout(
          handleAdvance,
          0,
          action,
          textArray[currentTextIndex],
          currentTextIndex
        );
        break;
      case 'complete':
        if (onComplete) onComplete();
        return;
      default:
        return;
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [
    currentCharIndex,
    displayedText,
    isDeleting,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
    textArray,
    currentTextIndex,
    loop,
    initialDelay,
    isVisible,
    reverseMode,
    variableSpeed,
    onSentenceComplete,
    handleDeleteTick,
    handleTypeTick,
    handleStartDeleting,
    handleAdvance,
    getRandomSpeed,
    onComplete
  ]);

  return {
    displayedText,
    currentTextIndex,
    currentCharIndex,
    isDeleting,
    textArray,
    setContainerElement,
    cursorRef
  };
}
